import head from 'lodash/head';
import join from 'lodash/join';
import memoize from 'lodash/memoize';
import { Component as ReactComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import type { ComponentClass } from 'react';

import ProjectGroupModule from '../../../../redux/project_grouping';
import type { APIError, ProjectGroup, ProjectGroupRelease } from '../../../../types';

type DispatchProps = {
  fetchRelease: (projectGroupId: number, id: number) => void;
};

type SelectorProps = {
  selectedRelease: ProjectGroupRelease;
  fetchedRelease: ProjectGroupRelease;
  fetchedReleaseLoading: boolean;
  fetchedReleaseLoadErr?: APIError;
};

export type HOCProps = {
  projectGroup: ProjectGroup;
} & DispatchProps & SelectorProps;

export type LoadedProjectGroupRelease = {
  data: ProjectGroupRelease;
  loading: boolean;
  error?: APIError;
}

export type ExpectedComponentProps = {
  projectGroup: ProjectGroup;
  release: LoadedProjectGroupRelease;
}

/**
 * withSelectedRelease is an HOC that will wrap the provided component
 * and provide it with the selected release from the current release group if appropriate,
 * otherwise it will provide it with the latest release from the current release group if available.
 */
const hoc = (Component: ComponentClass<ExpectedComponentProps>) => (
  class WithSelectedReleaseHOC extends ReactComponent<HOCProps> {
    get selectedRelease(): LoadedProjectGroupRelease {
      const { projectGroup, fetchedReleaseLoadErr, fetchedReleaseLoading } = this.props;
      const selectedRelease = this.props.selectedRelease || this.props.fetchedRelease;

      if (!selectedRelease && projectGroup) {
        const release = head(projectGroup.releases);
        this.fetchRelease(projectGroup.id, release.id);
      }

      return {
        data: selectedRelease || {} as ProjectGroupRelease,
        error: fetchedReleaseLoadErr,
        loading: fetchedReleaseLoading,
      };
    }

    fetchRelease = memoize(this.props.fetchRelease, join);

    render() {
      const { projectGroup: projectGroup = {} as ProjectGroup } = this.props;
      return <Component projectGroup={projectGroup} release={this.selectedRelease} />;
    }
  }
);

const mapStateToProps = createStructuredSelector({
  selectedRelease: ProjectGroupModule.selectors.getSelectedRelease,
  fetchedRelease: ProjectGroupModule.selectors.getRelease,
  fetchedReleaseLoading: ProjectGroupModule.selectors.getReleaseIsLoading,
  fetchedReleaseLoadErr: ProjectGroupModule.selectors.getReleaseError,
});

const mapDispatchToProps = {
  fetchRelease: ProjectGroupModule.actions.fetchRelease,
};

export const withSelectedRelease = compose(connect(mapStateToProps, mapDispatchToProps), hoc);
